import React, { useState } from 'react';

import Link from 'next/link';

import {
    ClipboardTick,
    ShieldTick,
    Health,
    Drop,
    Setting2,
    Calendar,
    Kanban,
    Judge,
    Box1,
    DocumentText,
    Book1,
    Routing,
    ArrowRight,
} from 'iconsax-react';

import classes from './PharmaDomains.module.scss';

// Shared definition of the three related links – the label/icon are the same
// for every domain, only the hrefs (set per domain in `links` below) differ.
const RELATED_LINK_META = [
    { id: 'assessment', label: 'Related Assessment', Icon: DocumentText },
    { id: 'courses', label: 'Related Courses', Icon: Book1 },
    { id: 'careerPath', label: 'Related Career Path', Icon: Routing },
];

// TODO: replace the placeholder '#' hrefs with the real destinations per domain.
const DOMAINS = [
    {
        id: 'gmp',
        label: 'Good Manufacturing Practices (GMP)',
        title: 'Good Manufacturing Practices (GMP)',
        description: 'Assess your knowledge, build focused skills, and follow a related career path in this pharma domain.',
        Icon: ClipboardTick,
        links: {
            assessment: '/assessments/assessment-e3784e4b-59e8-4816-b2ed-2441b155ae03/gmp-assessment',
            courses: '/explore?featured_contents=true&content=lecture&search_keyword=gmp',
            careerPath: '/career-path/1/gmp-basics',
        },
    },
    {
        id: 'quality-assurance',
        label: 'Quality Assurance',
        title: 'Quality Assurance',
        description: 'Assess your knowledge, build focused skills, and follow a related career path in this pharma domain.',
        Icon: ShieldTick,
        links: {
            assessment: '/assessments/assessment-db4d0b4f-c69b-41eb-b431-329224fb1e15/qa-specialist-assessment',
            courses: '/explore?featured_contents=true&content=lecture&search_keyword=Quality+Assurance',
            careerPath: '/career-path/2/quality-assurance-specialist',
        },
    },
    {
        id: 'pharmacovigilance',
        label: 'Pharmacovigilance / Drug Safety',
        title: 'Pharmacovigilance / Drug Safety',
        description: 'Assess your knowledge, build focused skills, and follow a related career path in this pharma domain.',
        Icon: Health,
        links: {
            assessment: '/assessments/assessment-b0180250-5085-472b-afac-37001b72b473/pharmacovigilance-assessment',
            courses: '/explore?featured_contents=true&content=lecture&search_keyword=pharmacovigilance',
            careerPath: '/career-path/6/pharmacovigilance-specialist-drug-safety',
        },
    },
    {
        id: 'cleaning-validation',
        label: 'Cleaning Validation',
        title: 'Cleaning Validation',
        description: 'Assess your knowledge, build focused skills, and follow a related career path in this pharma domain.',
        Icon: Drop,
        links: {
            assessment: '/assessments/assessment-b823dc3c-c50e-4c68-84b7-3ed833a810ad/cleaning-validation-assessment',
            courses: '/explore?featured_contents=true&content=lecture&search_keyword=Cleaning+Validation',
            careerPath: '/career-path/7/cleaning-validation-specialist',
        },
    },
    {
        id: 'equipment-qualification',
        label: 'Equipment Qualification',
        title: 'Equipment Qualification',
        description: 'Assess your knowledge, build focused skills, and follow a related career path in this pharma domain.',
        Icon: Setting2,
        links: {
            assessment: '/assessments/assessment-df87522b-72c3-41ba-ae38-0a3636b08bfd/equipment-qualification-assessment',
            courses: '/explore?featured_contents=true&content=lecture&search_keyword=Equipment+Qualification',
            careerPath: '/career-path/5/equipment-qualification-specialist',
        },
    },
    {
        id: 'production-planning',
        label: 'Production Planning',
        title: 'Production Planning',
        description: 'Assess your knowledge, build focused skills, and follow a related career path in this pharma domain.',
        Icon: Calendar,
        links: {
            assessment: '/assessments/assessment-7c87682f-f954-4a48-ad55-abeeb09ddb2f/pharma-production-planner-assessment',
            courses: '/explore?featured_contents=true&content=lecture&search_keyword=Production+Planning',
            careerPath: '/career-path/4/pharma-production-planner',
        },
    },
    {
        id: 'project-management',
        label: 'Pharmaceutical Project Management',
        title: 'Pharmaceutical Project Management',
        description: 'Assess your knowledge, build focused skills, and follow a related career path in this pharma domain.',
        Icon: Kanban,
        links: {
            assessment: '/assessments/assessment-f0bc3000-2bfd-4719-903f-84e43801f464/pharma-project-manager-assessment',
            courses: '/explore?featured_contents=true&content=lecture&search_keyword=Project+Management',
            careerPath: '/career-path/3/pharmaceutical-project-manager',
        },
    },
    {
        id: 'regulatory-affairs',
        label: 'Regulatory Affairs',
        title: 'Regulatory Affairs',
        description: 'Assess your knowledge, build focused skills, and follow a related career path in this pharma domain.',
        Icon: Judge,
        links: {
            assessment: '/assessments',
            courses: '/explore?featured_contents=true&content=lecture&search_keyword=Regulatory+Affairs',
            careerPath: '/career-path',
        },
    },
    {
        id: 'batch-management',
        label: 'Batch Management',
        title: 'Batch Management',
        description: 'Assess your knowledge, build focused skills, and follow a related career path in this pharma domain.',
        Icon: Box1,
        links: {
            assessment: '/assessments',
            courses: '/explore?featured_contents=true&content=lecture&search_keyword=Batch+Management',
            careerPath: '/career-path',
        },
    },
];

const PharmaDomains = () => {
    const [activeId, setActiveId] = useState(DOMAINS[0].id);
    const activeDomain = DOMAINS.find((domain) => domain.id === activeId) || DOMAINS[0];
    const ActiveIcon = activeDomain.Icon;

    return (
        <div className='container'>
            <section className={classes['domains']}>
                <div className={classes['domains__head']}>
                    <h2>Explore Pharma Learning Domains</h2>
                    <p>Deepen your professional expertise in specialized pharmaceutical areas through connected courses, assessments, and career tools.</p>
                </div>

                <div className={classes['domains__body']}>
                    <div className={classes['domains__nav']} role='tablist' aria-label='Pharma learning domains'>
                        {DOMAINS.map((domain) => (
                            <button
                                key={domain.id}
                                type='button'
                                role='tab'
                                aria-selected={domain.id === activeId}
                                className={`${classes['domains__nav-item']} ${domain.id === activeId ? classes['is-active'] : ''}`}
                                onClick={() => setActiveId(domain.id)}
                            >
                                {domain.label}
                            </button>
                        ))}
                    </div>

                    <div className={classes['domains__panel']} role='tabpanel'>
                        <div className={classes['domains__panel-icon']}>
                            <ActiveIcon size={32} color='#faa61a' />
                        </div>
                        <h3>{activeDomain.title}</h3>
                        <p>{activeDomain.description}</p>

                        <div className={classes['domains__links']}>
                            {RELATED_LINK_META.map(({ id, label, Icon }) => (
                                <Link
                                    key={id}
                                    href={activeDomain.links[id]}
                                    className={classes['domains__link']}
                                >
                                    <span className={classes['domains__link-icon']}>
                                        <Icon size={22} color='#faa61a' />
                                    </span>
                                    <span className={classes['domains__link-label']}>{label}</span>
                                    <ArrowRight size={20} color='#faa61a' className={classes['domains__link-arrow']} />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PharmaDomains;
